import { useState, useEffect } from 'react';
import { apiClient } from '../App';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Mail, ShieldCheck } from 'lucide-react';

/**
 * Email Notification Preferences (Phase 2A)
 *
 * Single opt-out toggle that reads/writes channels.email.enabled.
 * - Existing users default to ON (email enabled).
 * - Opt-out only happens by explicit user action.
 * - In-app notifications are always on.
 * - Account/security emails (password reset, email verification) are sent
 *   through a separate path and are NOT affected by this toggle.
 */
const EmailNotificationSettings = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [emailEnabled, setEmailEnabled] = useState(true);

  useEffect(() => {
    fetchPreference();
  }, []);

  const fetchPreference = async () => {
    try {
      setLoading(true);
      const res = await apiClient.get('/user/email-preferences');
      setEmailEnabled(res.data.email_enabled !== false);
    } catch (err) {
      console.error('Failed to fetch email preference:', err);
      toast.error('Failed to load email settings');
    } finally {
      setLoading(false);
    }
  };

  const handleToggle = async (checked) => {
    const oldValue = emailEnabled;
    setEmailEnabled(checked);
    try {
      setSaving(true);
      const res = await apiClient.post('/user/email-preferences', { enabled: checked });
      setEmailEnabled(res.data.email_enabled !== false);
      toast.success(checked ? 'Email notifications enabled' : 'Email notifications turned off');
    } catch (err) {
      setEmailEnabled(oldValue);
      console.error('Failed to update email preference:', err);
      toast.error('Failed to update email setting');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="p-4 text-center text-zinc-500" data-testid="email-settings-loading">
        Loading email settings...
      </div>
    );
  }

  return (
    <div className="p-6 bg-white rounded-xl border border-zinc-200" data-testid="email-notification-settings">
      <div className="flex items-center justify-between">
        <div className="flex items-start gap-3">
          <Mail className="w-5 h-5 text-orange-500 mt-0.5" />
          <div>
            <Label htmlFor="email-enabled-toggle" className="font-semibold text-zinc-900 cursor-pointer">
              Email Notifications
            </Label>
            <p className="text-sm text-zinc-500 mt-0.5">
              Game updates, reminders, and cancellations by email.
            </p>
          </div>
        </div>
        <Switch
          id="email-enabled-toggle"
          checked={emailEnabled}
          onCheckedChange={handleToggle}
          disabled={saving}
          data-testid="toggle-email-enabled"
        />
      </div>

      <div className="bg-blue-50 border border-blue-100 rounded-lg p-3 mt-4 flex items-start gap-2">
        <ShieldCheck className="w-4 h-4 text-blue-500 mt-0.5 shrink-0" />
        <p className="text-sm text-blue-800">
          In-app notifications are always on. Account &amp; security emails (like password
          resets and email verification) are always sent and not affected by this setting.
        </p>
      </div>
    </div>
  );
};

export default EmailNotificationSettings;
